import { get, map, filter, join, startsWith, pullAll } from "lodash";
import { Flex, Notification } from "orcs-design-system";
import pluralize from "pluralize";
import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";

// Layout container for the notifications below, not a notification itself.
const NotificationsWrapper = styled(Flex)`
  transform: translateX(-50%);
`;

// The wrapper does the floating, so each toast carries its own lift. ORCS only
// ships this shadow behind `floating`, which also sets `position: fixed` and
// would take every toast out of flow, collapsing the column onto itself.
const Toast = styled(Notification)`
  box-shadow: ${themeGet("shadows.boxDefault")};
`;

const NotificationOnFailure = ({ failList }) => {
  const existingUsers = map(
    filter(failList, (res) => startsWith(res.reason, "existing member")),
    "email"
  );
  const invalidUsers = map(
    filter(failList, (res) => startsWith(res.reason, "invalid email")),
    "email"
  );
  const otherFailures = pullAll(map(failList, "email"), [
    ...existingUsers,
    ...invalidUsers,
  ]);

  const existingUsersMessage = `Failed to invite the following ${pluralize(
    "email",
    existingUsers.length
  )} already been invited: ${join(existingUsers, ",")}.`;

  const invalidUsersMessage = `Failed to invite the following ${pluralize(
    "email",
    invalidUsers.length
  )} in invalid format: ${join(invalidUsers, ",")}.`;

  const otherFailureMessage = `Failed to invite the following ${pluralize(
    "email",
    otherFailures.length
  )}. Please refresh the page and try again: ${join(otherFailures, ",")}.`;

  return (
    <>
      {existingUsers.length > 0 && (
        <Toast variant="error">{existingUsersMessage}</Toast>
      )}

      {invalidUsers.length > 0 && (
        <Toast variant="error">{invalidUsersMessage}</Toast>
      )}

      {otherFailures.length > 0 && (
        <Toast variant="error">{otherFailureMessage}</Toast>
      )}
    </>
  );
};

const InviteNotification = ({ data }) => {
  const result = get(data, "result.invited", []);

  const successList = filter(result, "success");
  const successMessage = `Successfully sent invitation to ${
    successList.length
  } ${pluralize("email", successList.length)}.`;

  const failList = filter(result, (res) => !res.success);

  return (
    <NotificationsWrapper
      flexDirection="column"
      gap="r"
      position="fixed"
      bottom="20px"
      left="50%"
      zIndex="13"
    >
      {successList.length > 0 && (
        <Toast variant="success">{successMessage}</Toast>
      )}

      {failList.length > 0 && <NotificationOnFailure failList={failList} />}
    </NotificationsWrapper>
  );
};

NotificationOnFailure.propTypes = {
  failList: PropTypes.array.isRequired,
};

InviteNotification.propTypes = {
  data: PropTypes.object.isRequired,
};

export default InviteNotification;
